import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Users, UserPlus, Trash2, Calendar, Pencil } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { teamApi } from '@/services/teamApi'
import type { Team, TeamInvitation, TeamMember } from '@/types'
import { useAuth } from '@/hooks/useAuth'
import { StaffAvailabilityPanel } from '@/components/dashboard/StaffAvailabilityPanel'

export default function TeamManager() {
  const { t } = useTranslation('dashboard')
  const { user } = useAuth()
  const [team, setTeam] = useState<Team | null>(null)
  const [invitations, setInvitations] = useState<TeamInvitation[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<'admin' | 'member'>('member')
  const [busy, setBusy] = useState(false)
  const [scheduleUserId, setScheduleUserId] = useState<string | null>(null)
  const [editingName, setEditingName] = useState(false)
  const [teamName, setTeamName] = useState('')

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      const teams = await teamApi.getMyTeams()
      const primary = teams[0] ?? null
      setTeam(primary)
      if (primary) {
        try {
          const pending = await teamApi.listInvitations(primary.id)
          setInvitations(pending)
        } catch {
          setInvitations([])
        }
      } else {
        setInvitations([])
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load team')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  useEffect(() => {
    if (team?.name) {
      setTeamName(team.name)
    }
  }, [team?.name])

  const membership = team?.members?.find((m) => m.user_id === user?.id)
  const canManage =
    team?.owner_id === user?.id || membership?.role === 'admin' || membership?.role === 'owner'

  const handleCreate = async () => {
    setBusy(true)
    setError(null)
    try {
      const created = await teamApi.createTeam({})
      setTeam(created)
      setInvitations([])
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.errors?.profile_id?.[0] || 'Failed to create team')
    } finally {
      setBusy(false)
    }
  }

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!team || !inviteEmail.trim()) return
    setBusy(true)
    setError(null)
    try {
      await teamApi.invite(team.id, inviteEmail.trim(), inviteRole)
      setInviteEmail('')
      const pending = await teamApi.listInvitations(team.id)
      setInvitations(pending)
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.errors?.email?.[0] || 'Failed to send invite')
    } finally {
      setBusy(false)
    }
  }

  const handleRoleChange = async (member: TeamMember, role: 'admin' | 'member') => {
    if (!team) return
    setBusy(true)
    try {
      await teamApi.updateMemberRole(team.id, member.id, role)
      await load()
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update role')
    } finally {
      setBusy(false)
    }
  }

  const handleRemove = async (member: TeamMember) => {
    if (!team) return
    setBusy(true)
    try {
      await teamApi.removeMember(team.id, member.id)
      await load()
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to remove member')
    } finally {
      setBusy(false)
    }
  }

  const handleRevoke = async (invitation: TeamInvitation) => {
    if (!team) return
    setBusy(true)
    try {
      await teamApi.revokeInvitation(team.id, invitation.id)
      setInvitations((prev) => prev.filter((i) => i.id !== invitation.id))
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to revoke invitation')
    } finally {
      setBusy(false)
    }
  }

  const handleRenameTeam = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!team || !teamName.trim()) return
    setBusy(true)
    setError(null)
    try {
      const updated = await teamApi.updateTeam(team.id, { name: teamName.trim() })
      setTeam(updated)
      setEditingName(false)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update team name')
    } finally {
      setBusy(false)
    }
  }

  if (loading) {
    return <div className="p-8">{t('team.loading', { defaultValue: 'Loading team…' })}</div>
  }

  return (
    <div className="p-6 lg:p-8 max-w-3xl">
      <div className="flex items-center gap-3 mb-6">
        <Users className="w-6 h-6 text-primary" />
        <div>
          <h1 className="text-2xl font-semibold">{t('team.title', { defaultValue: 'Team' })}</h1>
          <p className="text-sm text-muted-foreground">
            {t('team.subtitle', { defaultValue: 'Invite staff and manage roles for your business.' })}
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {!team ? (
        <div className="rounded-lg border border-border p-6 space-y-4">
          <p className="text-muted-foreground">
            {t('team.empty', {
              defaultValue: 'Create a team to invite staff who can manage bookings with you.',
            })}
          </p>
          <Button onClick={handleCreate} disabled={busy}>
            {t('team.create', { defaultValue: 'Create team' })}
          </Button>
        </div>
      ) : (
        <div className="space-y-8">
          <div className="rounded-lg border border-border p-6">
            <div className="flex items-start justify-between gap-3 mb-4">
              {editingName && canManage ? (
                <form onSubmit={handleRenameTeam} className="flex-1 flex items-center gap-2">
                  <Input
                    value={teamName}
                    onChange={(e) => setTeamName(e.target.value)}
                    placeholder={t('team.rename.placeholder', { defaultValue: 'Team name' })}
                    className="max-w-xs"
                  />
                  <Button type="submit" size="sm" disabled={busy || !teamName.trim()}>
                    {t('team.rename.save', { defaultValue: 'Save' })}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setEditingName(false)
                      setTeamName(team.name || '')
                    }}
                  >
                    {t('team.rename.cancel', { defaultValue: 'Cancel' })}
                  </Button>
                </form>
              ) : (
                <>
                  <div>
                    <h2 className="font-medium mb-1">{team.name}</h2>
                    <p className="text-sm text-muted-foreground">
                      {team.profile?.business_name || team.slug}
                    </p>
                  </div>
                  {canManage && (
                    <button
                      type="button"
                      onClick={() => setEditingName(true)}
                      className="p-1 text-muted-foreground hover:text-primary"
                      aria-label={t('team.rename.edit', { defaultValue: 'Edit team name' })}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                  )}
                </>
              )}
            </div>

            <h3 className="text-sm font-medium mb-3">
              {t('team.members', { defaultValue: 'Members' })}
            </h3>
            <ul className="space-y-3">
              {(team.members || []).map((member) => (
                <li
                  key={member.id}
                  className="flex items-center justify-between gap-3 rounded-md bg-muted/40 px-3 py-2"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">
                      {member.user?.full_name || member.user?.email || member.user_id}
                    </p>
                    <p className="text-xs text-muted-foreground">{member.user?.email}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {canManage && member.role !== 'owner' && (
                      <button
                        type="button"
                        className="p-1 text-muted-foreground hover:text-primary"
                        onClick={() =>
                          setScheduleUserId(
                            scheduleUserId === member.user_id ? null : member.user_id
                          )
                        }
                        aria-label="Edit schedule"
                        title={t('team.schedule.edit', { defaultValue: 'Edit schedule' })}
                      >
                        <Calendar className="w-4 h-4" />
                      </button>
                    )}
                    {member.role === 'owner' || !canManage ? (
                      <span className="text-xs uppercase tracking-wide text-muted-foreground">
                        {member.role}
                      </span>
                    ) : (
                      <select
                        className="text-sm rounded border border-border bg-card px-2 py-1"
                        value={member.role}
                        disabled={busy}
                        onChange={(e) =>
                          handleRoleChange(member, e.target.value as 'admin' | 'member')
                        }
                      >
                        <option value="admin">admin</option>
                        <option value="member">member</option>
                      </select>
                    )}
                    {canManage && member.role !== 'owner' && (
                      <button
                        type="button"
                        className="p-1 text-muted-foreground hover:text-destructive"
                        onClick={() => handleRemove(member)}
                        disabled={busy}
                        aria-label="Remove member"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>

            {scheduleUserId && (
              <StaffAvailabilityPanel
                userId={scheduleUserId}
                memberName={
                  team.members?.find((m) => m.user_id === scheduleUserId)?.user?.full_name ||
                  scheduleUserId
                }
                onClose={() => setScheduleUserId(null)}
              />
            )}
          </div>

          {canManage && (
            <>
              <form onSubmit={handleInvite} className="rounded-lg border border-border p-6 space-y-4">
                <div className="flex items-center gap-2">
                  <UserPlus className="w-4 h-4" />
                  <h3 className="font-medium">
                    {t('team.invite', { defaultValue: 'Invite staff' })}
                  </h3>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <input
                    type="email"
                    required
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="colleague@example.com"
                    className="flex-1 rounded-md border border-border bg-card px-3 py-2 text-sm"
                  />
                  <select
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value as 'admin' | 'member')}
                    className="rounded-md border border-border bg-card px-3 py-2 text-sm"
                  >
                    <option value="member">member</option>
                    <option value="admin">admin</option>
                  </select>
                  <Button type="submit" disabled={busy}>
                    {t('team.sendInvite', { defaultValue: 'Send invite' })}
                  </Button>
                </div>
              </form>

              {invitations.length > 0 && (
                <div className="rounded-lg border border-border p-6">
                  <h3 className="font-medium mb-3">
                    {t('team.pending', { defaultValue: 'Pending invitations' })}
                  </h3>
                  <ul className="space-y-2">
                    {invitations.map((inv) => (
                      <li key={inv.id} className="flex items-center justify-between text-sm">
                        <span>
                          {inv.email}{' '}
                          <span className="text-muted-foreground">({inv.role})</span>
                        </span>
                        <button
                          type="button"
                          className="text-destructive hover:underline"
                          onClick={() => handleRevoke(inv)}
                          disabled={busy}
                        >
                          {t('team.revoke', { defaultValue: 'Revoke' })}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
